/**
 * Zugriff auf das Figurenmodell.
 *
 * Diese Funktionen sind die **einzige** Stelle, an der die Engine Figurenwerte liest.
 * Jede Regelfunktion fragt hier nach Reichweite, Schlagreichweite, Sprungfähigkeit und
 * Push-Parametern, statt sie an der Verwendungsstelle aus der Konfiguration zu greifen.
 *
 * Der Grund ist der Projektzweck: Figurenwerte ändern sich während des Balancings
 * ständig. Eine zentrale Auflösung heißt, dass eine Regeländerung an einer Stelle
 * passiert — und dass Marker wie `capture: 'sameAsMovement'` nicht in jeder
 * Regelfunktion erneut behandelt werden müssen.
 *
 * Alle Funktionen geben **Kopien** zurück. Die Konfiguration einer laufenden Partie ist
 * ein Snapshot (§53) und darf von keinem Aufrufer verändert werden — auch nicht aus
 * Versehen.
 */
/** Die fünf Figurenklassen in kanonischer Reihenfolge. */
export const PIECE_TYPES = ['flagBearer', 'guard', 'runner', 'jumper', 'pusher'];
function pieceConfig(config, type) {
    const piece = config.pieces[type];
    if (piece === undefined) {
        throw new Error(`RuleConfig enthält keinen Eintrag für die Figurenklasse '${type}'`);
    }
    return piece;
}
function copyRange(range) {
    return { min: range.min, max: range.max };
}
export function pieceCount(config, type) {
    return pieceConfig(config, type).count;
}
/** Figuren pro Spieler über alle Klassen. Bei den Defaults 9. */
export function totalPieceCount(config) {
    return PIECE_TYPES.reduce((sum, type) => sum + pieceCount(config, type), 0);
}
/** Bewegungsreichweite in geraden Hex-Schritten. Ohne `canJump` — das fragt `canJump` ab. */
export function movementRange(config, type) {
    return copyRange(pieceConfig(config, type).movement);
}
/**
 * Schlagreichweite, oder `undefined`, wenn die Figur **kein normales CAPTURE** ausführt.
 *
 * Löst den Marker `'sameAsMovement'` auf (Läufer und Springer). Regelcode soll nie
 * zwischen Marker und Zahlen unterscheiden müssen.
 *
 * `undefined` beim Fähnrich (schlägt nur den gegnerischen Fähnrich im Zentrum, §14) und
 * beim Stoßer (stößt statt zu schlagen, §18). Beides sind Regeln, keine Config-Werte.
 */
export function captureRange(config, type) {
    const capture = pieceConfig(config, type).capture;
    if (capture === undefined)
        return undefined;
    if (capture === 'sameAsMovement')
        return movementRange(config, type);
    return copyRange(capture);
}
/** Überspringt die Figur Zwischenfelder? Nur beim Springer, und dort abschaltbar. */
export function canJump(config, type) {
    return pieceConfig(config, type).movement.canJump === true;
}
/**
 * Wie `copyRange`, aber für Reichweiten ohne „unbegrenzt".
 *
 * Eigene Funktion statt eines Casts: `FiniteRange` ist kein `Range`, und eine Kopie darf den
 * Typ nicht verbreitern — sonst wäre `push.range.max` am Ende wieder `number | null`.
 */
function copyFiniteRange(range) {
    return { min: range.min, max: range.max };
}
/** Push-Parameter, oder `undefined` für alle Figuren außer dem Stoßer. */
export function pushConfig(config, type) {
    const push = pieceConfig(config, type).push;
    if (push === undefined)
        return undefined;
    return {
        range: copyFiniteRange(push.range),
        distance: push.distance,
        pusherAdvance: push.pusherAdvance,
        canPushOffBoard: push.canPushOffBoard,
        canPushFlagBearerFromGoal: push.canPushFlagBearerFromGoal,
    };
}
/**
 * Unbegrenzte Reichweite — der Default des Läufers.
 *
 * Regelcode fragt diese Funktion und vergleicht nicht selbst gegen `null`: sonst steht an
 * jeder Verwendungsstelle eine Annahme darüber, was `null` bedeutet.
 */
export function isUnlimited(range) {
    return range.max === null;
}
//# sourceMappingURL=pieces.js.map